import { inject as service } from '@ember/service';
import { readOnly, gt } from '@ember/object/computed';
import Controller, { inject as controller } from '@ember/controller';
import { isNone, isEmpty, isPresent } from '@ember/utils';
import { action, computed } from '@ember/object';
import { waitForQueue } from 'ember-concurrency';
import { task } from 'ember-concurrency';
import DS from 'ember-data';
import integerToString from 'cio/utils/integer-to-string';
import ApiService from 'cio/services/api';
import CustomerService from 'cio/services/customer';
import NewsletterSetupController from 'cio/pods/broadcasts/newsletter/setup/controller';
import NewsletterModel from 'cio/models/newsletter';
import AnalyticsService from 'cio/services/analytics';
import { SerializedLegacyFilter } from 'cio/types/conditions/legacy-filter';
import serializeFilters from 'cio/utils/conditions/serialize/filter';
import { queryDecode, queryEncode } from 'cio/helpers/query';
import FormValidations from 'cio/utils/form-validations';
import FeaturesService from 'ember-feature-flags/services/features';
import EnvironmentService from 'cio/services/environment';
import { tracked } from '@glimmer/tracking';

const Validations = {
  rateLimitEmailRate: {
    dependentKeys: ['rateLimitTimePeriod'],
    validator(this: BroadcastsNewsletterSetupRecipientsIndexController, value: string | number) {
      value = parseInt(String(value));
      if (this._sendRate === 'limit' && isPresent(value) && (!Number.isInteger(value) || value < 1)) {
        return 'Send rate must be a positive, whole number';
      }

      return true;
    },
  },
  rateLimitTimePeriod(this: BroadcastsNewsletterSetupRecipientsIndexController, value: number | null) {
    if (this._sendRate === 'asap') {
      return true;
    }

    if (isEmpty(value)) {
      return 'Must select a rate limit time period';
    }

    return true;
  },
  sendToEveryone(this: BroadcastsNewsletterSetupRecipientsIndexController, sendToEveryone: boolean) {
    if (sendToEveryone || isPresent(this.model.filters)) {
      return true;
    }

    return 'Must choose a Segment to send to';
  },
};

const MINIMUM_TOTAL_PEOPLE_FOR_SUBSETTING = 500;

export enum TargetAudience {
  Subset = 'subset',
  Everyone = 'everyone',
}

export default class BroadcastsNewsletterSetupRecipientsIndexController extends Controller {
  @service api!: ApiService;
  @service store!: DS.Store;
  @service customer!: CustomerService;
  @service analytics!: AnalyticsService;
  @service features!: FeaturesService;
  @service environment!: EnvironmentService;

  validations!: FormValidations;

  @controller('broadcasts.newsletter.setup') newsletterSetupController!: NewsletterSetupController;

  declare model: NewsletterModel;

  _sendRate = 'asap'; // can be 'asap' or 'limit'
  initialSendRate = 'asap'; // A copy of _sendRate run in setupController, to keep track of isDirty changes
  supportedConditionTypes = ['segments_negatable', 'attribute', 'device_attribute', 'newsletter_recipients', 'message'];
  @tracked recipientCount = 0;
  @tracked recipientMessage = '';
  newsletterFilters?: SerializedLegacyFilter[];

  originalSendToUnsubscribed = false;
  originalSubscriptionTopicId: string | null = null;

  expandRandomSubset = false;

  @tracked hasAcceptedRisksOfSendingToEveryone = false;

  @computed('model.hasDeletedSubscriptionTopic', 'validations.hasErrors', 'needsToAcceptReputationRisk')
  get isInvalid() {
    return this.model.hasDeletedSubscriptionTopic || this.validations.hasErrors || this.needsToAcceptReputationRisk;
  }

  get conditionTypes() {
    let conditionTypes = this.supportedConditionTypes;
    if (!this.environment.enabledAction('push')) {
      conditionTypes = conditionTypes.filter((t) => t !== 'device_attribute');
    }
    return conditionTypes;
  }

  @computed('filterSelected', 'hasAcceptedRisksOfSendingToEveryone', 'recipientCount', 'fetchRecipients.isRunning')
  get needsToAcceptReputationRisk() {
    return (
      this.filterSelected === TargetAudience.Everyone &&
      !this.hasAcceptedRisksOfSendingToEveryone &&
      !this.fetchRecipients.isRunning &&
      this.recipientCount >= 20
    );
  }

  @computed('expandRandomSubset', 'model.sendPercentage')
  get showRandomSubset() {
    return this.expandRandomSubset || this.model.sendPercentage !== 100;
  }

  @gt('recipientCount', MINIMUM_TOTAL_PEOPLE_FOR_SUBSETTING) allowRandomSubset!: boolean;

  @readOnly('recipientCount') sendingTotalPeople!: number;

  @computed('recipientCount', 'sendingTotalPeople')
  get remainingTotalPeople() {
    return this.recipientCount - this.sendingTotalPeople;
  }

  @computed('isInvalid', 'needsToAcceptReputationRisk')
  get invalidErrorMessage() {
    if (!this.isInvalid) {
      return;
    }

    if (this.newsletterSetupController.isRedactedUserWhoCannotEdit === true) {
      return "Editing is disabled for this role. Please contact your team's admin to make changes.";
    }

    let errors = this.validations.errorsList as unknown as string[];
    if (this.needsToAcceptReputationRisk) {
      errors = [...errors, 'Please accept the risks of sending to everyone.'];
    }

    return `${errors.join('. ')}.`;
  }

  _disableSubset() {
    this.model.set('sendPercentage', 100);
    this.set('expandRandomSubset', false);
  }

  private async reloadRecipients() {
    await this.model.findRecipients();
    if (!this.allowRandomSubset && this.model.editable) {
      this._disableSubset();
    }
  }

  @integerToString('model.sendPercentage') sendPercentage!: string;

  get peopleFilters() {
    if (!this.model.serializedFilters) {
      return undefined;
    }

    return queryEncode(
      serializeFilters(queryDecode(this.model.serializedFilters) as SerializedLegacyFilter[], 'and', true),
    );
  }

  @computed('model.filters')
  get hasSegments() {
    let {
      model: { filters },
    } = this;
    return !isNone(filters) && !isEmpty(filters);
  }

  @computed('filterSelected', 'hasSegments')
  get showSendingDetails() {
    let { filterSelected } = this;

    if (isNone(filterSelected)) {
      return false;
    }

    if (filterSelected === TargetAudience.Subset) {
      return this.hasSegments;
    }

    return true;
  }

  @computed('model.sendToEveryone')
  get filterSelected() {
    return this.model.sendToEveryone === true ? TargetAudience.Everyone : TargetAudience.Subset;
  }

  @action
  recipientsChanged() {
    this.validations.validateAttribute('sendToEveryone');
    this.fetchRecipients.perform();
  }

  @computed(
    'model.hasDirtyAttributes',
    '_sendRate',
    'initialSendRate',
    'isSendToUnsubscribedDirty',
    'isSubscriptionTopicDirty',
  )
  get isDirty() {
    return (
      this.model.hasDirtyAttributes ||
      this._sendRate !== this.initialSendRate ||
      this.isSendToUnsubscribedDirty ||
      this.isSubscriptionTopicDirty
    );
  }

  @computed('originalSendToUnsubscribed', 'model.sendToUnsubscribed')
  private get isSendToUnsubscribedDirty() {
    return this.originalSendToUnsubscribed !== this.model.sendToUnsubscribed;
  }

  @computed('originalSubscriptionTopicId', 'model.subscriptionTopicId')
  private get isSubscriptionTopicDirty() {
    return this.originalSubscriptionTopicId !== this.model.subscriptionTopicId;
  }

  fetchRecipients = task({ restartable: true }, async () => {
    await waitForQueue('actions');

    let result = await this.store.query('customer', { filters: this.model.serializedFilters });
    this.recipientCount = (result as any).meta.pagination.total;
    this.recipientMessage = (result as any).meta.message;
  });

  resetState() {
    this.setProperties({
      expandRandomSubset: this.model.sendPercentage !== 100,
      _sendRate: this.initialSendRate,
    });
  }

  _rollbackFilterSpecificAttributes() {
    let { sendPercentage } = this.model;

    this.model.rollbackAttributes();

    this.model.setProperties({
      sendPercentage,
      sendToUnsubscribed: false,
    });
  }

  resetTask = task(async () => {
    this.model.rollbackAttributes();

    this.resetState();
    await this.reloadRecipients();
    this.analytics.track('broadcast', 'edit_discarded', { model: this.model, from: 'recipients' });
  });

  saveNewsletterTask = task(async () => {
    if (this._sendRate === 'asap') {
      this.model.setProperties({
        rateLimitEmailRate: null,
        rateLimitTimePeriod: null,
      });
    }

    this.model.set('updateType', 'recipients');
    await this.model.save();

    this.setProperties({
      initialSendRate: this._sendRate,
      originalSendToUnsubscribed: this.model.sendToUnsubscribed,
      originalSubscriptionTopicId: this.model.subscriptionTopicId,
    });
  });

  @action
  sendToEveryone() {
    this._rollbackFilterSpecificAttributes();
    this.updateFilters([]);
    this.model.set('sendToEveryone', true);
  }

  @action
  sendToSubset() {
    this._rollbackFilterSpecificAttributes();
    this.model.set('sendToEveryone', false);
    this.newsletterFilters = this.model.filters;
  }

  @action
  disableSubset() {
    this._disableSubset();
  }

  @action
  async updateFilters(conditions: SerializedLegacyFilter[]) {
    this.model.set('filters', conditions);
    this.set('newsletterFilters', conditions);

    await this.reloadRecipients();
    this.newsletterSetupController.findNewslettersUsedInPositiveConditionsTask.perform();
  }

  @action
  setSendRate(val: string) {
    this.set('_sendRate', val);

    if (val === 'asap') {
      this.validations.clearAttributeErrors('rateLimitEmailRate');
      this.validations.clearAttributeErrors('rateLimitTimePeriod');
    } else {
      this.validations.validateAttribute('rateLimitEmailRate');
      this.validations.validateAttribute('rateLimitTimePeriod');
    }
  }

  @action
  trackSegmentCreationStartedFrom(source: string) {
    this.analytics.track('segment', 'creation_started', { source });
  }

  @action
  onAudienceChange(subscriptionTopicId: string | null, sendToUnsubscribed: boolean) {
    this.model.setProperties({
      subscriptionTopicId,
      sendToUnsubscribed,
    });
  }

  constructor() {
    super(...arguments);

    this.set(
      'validations',
      FormValidations.create({
        validations: Validations,
        context: this,
        modelKey: 'model',
      }),
    );
  }
}
